import React from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import { CLIENT_COPY_LISTING_PHRASE } from './signupFloorPlanOrigin';
import { ANALYTICS_GREEN, analyticsGreenAlpha } from './analyticsColors';
import { buildFloorPlanPreviewCandidates } from './floorPlanPreviewUrl';

const formatRelative = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatDaysOnMarket = (days) => {
  if (days == null) return '—';
  if (days === 0) return 'Listed today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const STATUS_CHIP_STYLES = {
  active: { bgcolor: analyticsGreenAlpha(0.08), color: ANALYTICS_GREEN },
  pending: { bgcolor: 'rgba(217,119,6,0.1)', color: 'rgba(180,83,9,0.95)' },
  sold: { bgcolor: 'rgba(37,99,235,0.08)', color: 'rgba(29,78,216,0.92)' },
  withdrawn: { bgcolor: 'rgba(0,0,0,0.05)', color: 'rgba(0,0,0,0.55)' },
  expired: { bgcolor: 'rgba(0,0,0,0.05)', color: 'rgba(0,0,0,0.55)' },
  unknown: { bgcolor: 'rgba(0,0,0,0.04)', color: 'rgba(0,0,0,0.45)' },
};

/**
 * Compact label-over-value stat strip element. Exported so the merged
 * Floor Plans / Listings view (FloorPlanLibrarySection's "Linked" filter)
 * can render the listing-only stats with the same chrome — single source
 * of truth for status / days-on-market / inquiries / sessions etc.
 */
export const Stat = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(0,0,0,0.42)', lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(0,0,0,0.78)' }}>
      {value}
    </Typography>
  </Box>
);


const DENSE_CHIP_LABEL_SX = {
  fontSize: '0.75rem !important',
  lineHeight: '1.2 !important',
  paddingLeft: '6px !important',
  paddingRight: '6px !important',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  maxWidth: '100%',
  color: 'inherit !important',
};

export const ListingStatusChip = ({ status, label, compact = false, dense = false, onClick }) => {
  const chipStyle = STATUS_CHIP_STYLES[status] || STATUS_CHIP_STYLES.unknown;
  const chip = (
    <Chip
      size="small"
      label={label}
      sx={{
        height: dense ? 22 : compact ? 18 : 20,
        fontWeight: 600,
        borderRadius: dense ? '9999px' : undefined,
        flexShrink: dense ? 1 : 0,
        minWidth: 0,
        maxWidth: dense ? '100%' : undefined,
        width: dense ? '100%' : undefined,
        overflow: dense ? 'hidden' : undefined,
        ...(dense
          ? { '& .MuiChip-label': DENSE_CHIP_LABEL_SX }
          : {
              fontSize: compact ? '0.6rem' : '0.66rem',
              '& .MuiChip-label': {
                fontSize: `${compact ? '0.6rem' : '0.66rem'} !important`,
                color: 'inherit !important',
              },
            }),
        ...chipStyle,
      }}
    />
  );
  if (!onClick) return chip;
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label="Update property status"
      sx={{
        borderRadius: '9999px',
        flexShrink: dense ? 1 : 0,
        minWidth: 0,
        maxWidth: dense ? '100%' : undefined,
        display: 'block',
        overflow: 'hidden',
      }}
    >
      {chip}
    </ButtonBase>
  );
};

const ListingRow = ({
  listing,
  linkRecord,
  plan,
  partner,
  existingLinks,
  planByCustomerRef,
  onChangeListing,
  onDisconnect,
  onListingLinkSuccess,
  onEditListingStatus,
}) => {
  const ref = listing.customer_ref || '—';
  const linkId = linkRecord?._id || linkRecord?.id;
  const planName = plan?.name?.trim() || null;
  const showListingRefSubtitle =
    Boolean(ref && ref !== '—') &&
    (!planName || String(ref).trim().toLowerCase() !== planName.toLowerCase());
  const planThumb = buildFloorPlanPreviewCandidates(plan)[0] || null;
  const shares = listing.shareCount ?? listing.shares ?? 0;
  const downloads = listing.downloadCount ?? listing.downloads ?? 0;

  return (
    <Box
      sx={{
        py: 1.25,
        borderTop: '1px solid rgba(0,0,0,0.05)',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1.25} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
          {(planThumb || planName) && (
            <Avatar
              variant="rounded"
              src={planThumb || undefined}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                flexShrink: 0,
                fontSize: '0.85rem',
                bgcolor: 'rgba(0,0,0,0.05)',
                color: 'rgba(0,0,0,0.5)',
              }}
            >
              {planName?.[0] || 'F'}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.82)',
                  lineHeight: 1.2,
                  minWidth: 0,
                }}
                noWrap
              >
                {planName || `Listing ${ref}`}
              </Typography>
              <ListingStatusChip
                status={listing.status || 'unknown'}
                label={listing.statusLabel || 'Unknown status'}
                onClick={
                  linkId
                    ? () =>
                        onEditListingStatus?.({
                          linkId,
                          listingRef: ref,
                          planName: planName || `Listing ${ref}`,
                          mlsStatus: listing.mlsStatus,
                          customPropertyStatus: listing.customPropertyStatus,
                          onMarketDate: listing.onMarketDate,
                          onMarketDateSource: listing.onMarketDateSource,
                          offMarketDate: listing.offMarketDate,
                          mlsSyncEnabled: listing.mlsSyncEnabled,
                        })
                    : undefined
                }
              />
            </Stack>
            {showListingRefSubtitle ? (
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: 'rgba(0,0,0,0.5)',
                  lineHeight: 1.3,
                  mt: 0.15,
                }}
                noWrap
              >
                Listing {ref}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {linkId && (
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {plan && (
              <Button
                size="small"
                onClick={() =>
                  onEditListingStatus?.({
                    linkId,
                    listingRef: ref,
                    planName: planName || `Listing ${ref}`,
                    mlsStatus: listing.mlsStatus,
                    customPropertyStatus: listing.customPropertyStatus,
                    onMarketDate: listing.onMarketDate,
                    onMarketDateSource: listing.onMarketDateSource,
                    offMarketDate: listing.offMarketDate,
                    mlsSyncEnabled: listing.mlsSyncEnabled,
                  })
                }
                sx={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 0.75 }}
              >
                Manage
              </Button>
            )}
            <Button
              size="small"
              color="error"
              onClick={() => onDisconnect?.({ linkId, listingRef: ref, plan })}
              sx={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 0.75 }}
            >
              Unlink
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
        <Stat label="Days on market" value={formatDaysOnMarket(listing.daysOnMarket)} />
        <Stat
          label="Listing interest"
          value={(listing.inquiryCount ?? 0).toLocaleString()}
        />
        <Stat label="Views" value={(listing.galleryViews ?? 0).toLocaleString()} />
        <Stat label="Sessions" value={(listing.sessions ?? 0).toLocaleString()} />
        <Stat label="Shuffles" value={(listing.shuffles ?? 0).toLocaleString()} />
        <Stat label="Shares" value={shares.toLocaleString()} />
        <Stat label="Downloads" value={downloads.toLocaleString()} />
        <Stat label="Last activity" value={formatRelative(listing.lastActivityAt)} />
      </Stack>
    </Box>
  );
};

/**
 * Linked listings from GET /listings-summary (partner admin).
 *
 * The "Unlink" button raises the disconnect intent via `onDisconnect`; the
 * confirmation modal lives in the page (AnalyticsTwo) so the Floor plans
 * library and the Listings section share one cautionary modal.
 */
const ListingsSummarySection = ({
  loading = false,
  summary = null,
  layoutLinksByUuid = null,
  planByCustomerRef = null,
  partner = null,
  existingLinks = [],
  onManageLibraryClick,
  onChangeListing,
  onDisconnect,
  onListingLinkSuccess,
  onEditListingStatus,
}) => {
  const listings = summary?.listings || [];
  const total = summary?.total ?? listings.length;
  const hasLinks = total > 0;

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={hasLinks ? 200 : 120}
        sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)', mt: { xs: 1.5, sm: 2 } }}
      />
    );
  }

  return (
    <Box
      sx={{
        mt: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: { xs: 1.75, sm: 2 }, py: { xs: 1.25, sm: 1.5 } }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <HomeWorkOutlined sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)' }} />
            <Box>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(0,0,0,0.72)' }}>
                Listings
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)', mt: 0.15 }}>
                {hasLinks
                  ? `${total} listing${total !== 1 ? 's' : ''} linked this period`
                  : 'Metrics appear after you link a floor plan to a listing ID in Floor plans'}
              </Typography>
            </Box>
          </Stack>
          {onManageLibraryClick && (
            <Button
              size="small"
              startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
              onClick={onManageLibraryClick}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontSize: '0.78rem',
                flexShrink: 0,
              }}
            >
              Floor plans
            </Button>
          )}
        </Stack>

        <Typography
          sx={{
            fontSize: '0.72rem',
            color: 'rgba(0,0,0,0.45)',
            lineHeight: 1.45,
            px: { xs: 1.75, sm: 2 },
            pb: 1,
          }}
        >
          Totals at the top of this page include all floor plans on your site. This section only
          shows properties you have linked. Listing interest, shares, and downloads here are per listing
          for your selected date range. {CLIENT_COPY_LISTING_PHRASE}
        </Typography>

        <Box sx={{ px: { xs: 1.75, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
          {!hasLinks ? (
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.55 }}>
              No listings linked yet. Use Floor plans above to link a listing ID to a floor plan.
            </Typography>
          ) : (
            listings.map((listing) => (
              <ListingRow
                key={`${listing.customer_ref}-${listing.layoutUuid}`}
                listing={listing}
                linkRecord={
                  layoutLinksByUuid?.get?.(listing.layoutUuid) ||
                  layoutLinksByUuid?.[listing.layoutUuid]
                }
                plan={planByCustomerRef?.get?.(listing.customer_ref) || null}
                partner={partner}
                existingLinks={existingLinks}
                planByCustomerRef={planByCustomerRef}
                onChangeListing={onChangeListing}
                onDisconnect={onDisconnect}
                onListingLinkSuccess={onListingLinkSuccess}
                onEditListingStatus={onEditListingStatus}
              />
            ))
          )}
        </Box>
    </Box>
  );
};

export default ListingsSummarySection;
